import { useCallback, useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { ReorderSuggestion } from "../api/types";
import { NO_FIGURE, knownAmount } from "../utils/unknownCost";

interface ReorderSuggestionsPanelProps {
  /** Omit for MANAGER/FLORIST (their assignment wins server-side). */
  locationId?: string;
  /** When set, shows a "Prefill order lines" button that hands over the rows. */
  onPrefill?: (rows: ReorderSuggestion[]) => void;
  /**
   * G-132: the host already fetched the list (the inventory page needs the
   * count for its "Low" chip before this renders), so it hands the rows down
   * and the panel does not ask again. Omit it and the panel fetches, as the
   * new-delivery page still expects.
   */
  rows?: ReorderSuggestion[];
  /** G-132: drop the title — the chip that opened this view already names it. */
  bare?: boolean;
}

/**
 * Low-stock / suggested-order panel (Finalizing.MD T7+T8): items at or below
 * their reorder threshold with a usage-informed suggested quantity.
 */
export function ReorderSuggestionsPanel({
  locationId,
  onPrefill,
  rows: rowsFromHost,
  bare = false,
}: ReorderSuggestionsPanelProps) {
  const controlled = rowsFromHost !== undefined;
  const [ownRows, setRows] = useState<ReorderSuggestion[]>([]);
  const rows = rowsFromHost ?? ownRows;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.getReorderSuggestions(locationId));
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not load low-stock items.",
      );
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!controlled) void refresh();
  }, [controlled, refresh]);

  if (!controlled && loading) {
    return <div className="card muted">Checking stock levels…</div>;
  }
  if (!controlled && error) {
    return <div className="alert error">{error}</div>;
  }
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="card" style={{ display: "grid", gap: "var(--space-3)" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <div>
          {bare ? null : (
            <strong style={{ fontSize: 16 }}>
              Low stock — {rows.length} item{rows.length === 1 ? "" : "s"} below
              threshold
            </strong>
          )}
          <p className="muted" style={{ margin: bare ? 0 : "var(--space-1) 0 0", fontSize: 13 }}>
            Suggested quantities come from the reorder threshold and the last 28
            days of usage.
          </p>
        </div>
        {onPrefill ? (
          <button
            type="button"
            className="button"
            onClick={() => onPrefill(rows)}
          >
            Prefill order lines
          </button>
        ) : null}
      </div>

      {/*
        * G-92 — on a phone this table showed three of its eight columns inside
        * a 263 px card (717 px of table), so "how many should I order?" was two
        * sideways scrolls away from the item's name. The same rows read as
        * lines here: what it is, then "0 of 12 · order 24". The table returns
        * above 900 px, where all eight columns fit and comparing them is the
        * point.
        */}
      <ul className="reorder-lines">
        {rows.map((r) => (
          <li key={r.itemId} className="reorder-line">
            <div className="reorder-line-who">
              <span className="reorder-line-name">{r.itemName}</span>
              <span className="reorder-line-meta">
                {r.category.label}
                {r.lastSupplier ? ` · ${r.lastSupplier.name}` : ""}
                {knownAmount(r.lastUnitCostEur) != null ? ` · €${r.lastUnitCostEur}` : ""}
              </span>
            </div>
            <div className="reorder-line-numbers">
              <span className="reorder-line-have">
                <b>{r.qtyOnHand}</b> of {r.reorderThreshold}
              </span>
              <span className="reorder-line-order">order {r.suggestedQty}</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="reorder-table" style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th>On hand</th>
              <th>Threshold</th>
              <th>Weekly usage</th>
              <th>Suggested</th>
              <th>Last supplier</th>
              <th>Last cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.itemId}>
                <td>{r.itemName}</td>
                <td className="muted">{r.category.label}</td>
                <td>
                  <span className="badge danger">{r.qtyOnHand}</span>
                </td>
                <td>{r.reorderThreshold}</td>
                <td className="muted">{r.weeklyUsage}</td>
                <td>
                  <strong>{r.suggestedQty}</strong>
                </td>
                <td className="muted">{r.lastSupplier?.name ?? "—"}</td>
                <td className="muted">
                  {knownAmount(r.lastUnitCostEur) != null ? `€${r.lastUnitCostEur}` : NO_FIGURE}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
